import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const shortDate = (iso) => { try { return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }); } catch { return iso; } };
const EMPTY = { date: new Date().toISOString().slice(0, 10), weight: "", body_fat: "", muscle_mass: "", waist: "", chest: "", hip: "", arm: "", note: "" };

export default function Measurements({ memberId, canEdit = false }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/members/${memberId}/measurements`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [memberId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { if (v !== "") payload[k] = k === "note" || k === "date" ? v : Number(v); });
      await api.post(`/members/${memberId}/measurements`, payload);
      toast.success("Ölçüm eklendi");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (items === null) return <Skeleton className="h-64 rounded-2xl bg-[#13131C] mt-4" />;

  const chartData = items.map((m) => ({ date: shortDate(m.date), weight: m.weight, body_fat: m.body_fat }));
  const latest = items[items.length - 1];

  return (
    <div className="mt-4" data-testid="measurements-panel">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-display font-bold text-white text-lg">Vücut Ölçümleri</h4>
        {canEdit && <Button onClick={() => setOpen(true)} data-testid="add-measurement-button" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold rounded-xl h-9"><Plus size={16} className="mr-1" /> Ölçüm Ekle</Button>}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-[#68687B] rounded-2xl border border-[#222230] bg-[#13131C]">Henüz ölçüm kaydı yok.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Metric label="Kilo" value={latest.weight} unit="kg" />
            <Metric label="Yağ Oranı" value={latest.body_fat} unit="%" />
            <Metric label="Kas" value={latest.muscle_mass} unit="kg" />
            <Metric label="Bel" value={latest.waist} unit="cm" />
          </div>
          <div className="rounded-2xl bg-[#13131C] border border-[#222230] p-4">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData} margin={{ left: -16, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E1E2A" vertical={false} />
                <XAxis dataKey="date" stroke="#68687B" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#68687B" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#181824", border: "1px solid #2D2D3F", borderRadius: 12 }} labelStyle={{ color: "#A0A0B2" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="weight" name="Kilo (kg)" stroke="#FF5500" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="body_fat" name="Yağ (%)" stroke="#3B82F6" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 rounded-2xl border border-[#222230] overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#181824] text-[#68687B] text-xs uppercase">
                <tr><th className="px-4 py-2 text-left">Tarih</th><th className="px-4 py-2">Kilo</th><th className="px-4 py-2">Yağ%</th><th className="px-4 py-2">Kas</th><th className="px-4 py-2">Bel</th></tr>
              </thead>
              <tbody>
                {[...items].reverse().map((m, i) => (
                  <tr key={m.id} className={`border-t border-[#1A1A24] text-center ${i % 2 ? "bg-[#161622]" : "bg-[#13131C]"}`}>
                    <td className="px-4 py-2 text-left text-white">{shortDate(m.date)}</td>
                    <td className="px-4 py-2 font-mono text-white">{m.weight ?? "—"}</td>
                    <td className="px-4 py-2 font-mono text-[#A0A0B2]">{m.body_fat ?? "—"}</td>
                    <td className="px-4 py-2 font-mono text-[#A0A0B2]">{m.muscle_mass ?? "—"}</td>
                    <td className="px-4 py-2 font-mono text-[#A0A0B2]">{m.waist ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">Yeni Ölçüm</DialogTitle><DialogDescription className="text-[#68687B]">Üyenin güncel vücut ölçümlerini gir.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-1">
            <F label="Tarih"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white [color-scheme:dark]" /></F>
            <F label="Kilo (kg)"><Input type="number" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} data-testid="measurement-weight" className="bg-[#0E0E14] border-[#262636] text-white" /></F>
            <F label="Yağ Oranı (%)"><Input type="number" value={form.body_fat} onChange={(e) => setForm({ ...form, body_fat: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></F>
            <F label="Kas (kg)"><Input type="number" value={form.muscle_mass} onChange={(e) => setForm({ ...form, muscle_mass: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></F>
            <F label="Bel (cm)"><Input type="number" value={form.waist} onChange={(e) => setForm({ ...form, waist: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></F>
            <F label="Göğüs (cm)"><Input type="number" value={form.chest} onChange={(e) => setForm({ ...form, chest: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></F>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#A0A0B2]">İptal</Button>
            <Button onClick={save} disabled={saving} data-testid="measurement-submit" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold">{saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ label, value, unit }) {
  return (
    <div className="rounded-xl bg-[#13131C] border border-[#222230] p-3">
      <p className="text-xs text-[#68687B]">{label}</p>
      <p className="font-mono font-bold text-lg text-white mt-0.5">{value ?? "—"}<span className="text-xs text-[#68687B] ml-1">{unit}</span></p>
    </div>
  );
}
function F({ label, children }) { return <div><label className="text-xs text-[#A0A0B2] mb-1 block">{label}</label>{children}</div>; }
